import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import Logger from 'observability/Logger';

import { SuiContainer } from '@splashthat/sui';
import CubeLoaderOverlay from 'common/components/CubeLoaderOverlay';
import CubeLoader, { INLINE_SIZE, MODAL_SIZE } from 'common/components/CubeLoader';
import NoResultsFound from 'common/components/NoResultsFound';
import { colorAppShadedBackground } from 'common/styles/colors';
import usePrevious from 'utils/hooks/usePrevious';

import GiphyApiClient from 'lib/GiphyApiClient';

import SearchBar from '../SearchBar';

import { Slide, InfiniteScrollContainer, Gif } from './GifSlide.styles';

const INPUT_DEBOUNCE_TIMEOUT = 250;
const SEARCH_PLACEHOLDER = 'Search Giphy for the perfect GIF';
const TRY_LATER_MESSAGE = 'Service unavailable. Try later.';
const BASE_CLASS_NAME = 'GifSlide';

const LOG = new Logger('GifSlide');

function parseGiphyResults(results) {
    return (results || []).map((result) => ({
        url: result.images.downsized.url,
        id: result.id,
        title: result.title,
    }));
}

export default function GifSlide({
    isActive,
    gifs,
    gifSearchValue,
    gifSearchError,
    setGifs,
    setGifSearchError,
    setGifSearchValue,
    onImageSelected,
}) {
    const isPreviouslyActive = usePrevious(isActive);
    const previousGifSearchValue = usePrevious(gifSearchValue);
    const [isSearching, setIsSearching] = useState(false);
    const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
    const gifListRef = useRef(null);
    const giphyApiClient = useRef(new GiphyApiClient()).current;

    const scrollGifListToTop = () => {
        if (gifListRef.current?.scrollContainer?.container?.scrollTop) {
            gifListRef.current.scrollContainer.container.scrollTop = 0;
        }
    };

    const search = async (term, isAddedToExisting = false) => {
        const offset = isAddedToExisting ? gifs.length : 0;

        if (term) {
            setIsSearching(true);
        }

        setIsFetchingNextPage(isAddedToExisting);

        try {
            let results;
            if (term) {
                results = await giphyApiClient.search(term, offset);
            } else {
                results = await giphyApiClient.trending(offset);
            }

            const parsedResults = [...(offset ? gifs : []), ...parseGiphyResults(results)];

            gifSearchError && setGifSearchError('');

            setGifs(parsedResults);

            !isAddedToExisting && scrollGifListToTop();

            setIsSearching(false);
            setIsFetchingNextPage(false);
        } catch (error) {
            LOG.error('Error on search', error);
            setIsSearching(false);
            setIsFetchingNextPage(false);

            if (gifs.length) {
                setGifs([]);
            } else {
                setGifSearchError(TRY_LATER_MESSAGE);
            }
        }
    };

    const searchDebounced = useCallback(debounce(search, INPUT_DEBOUNCE_TIMEOUT), []);

    const onGifSelected = (gif) => {
        const imageUrl = gif.url;
        onImageSelected(imageUrl);
    };

    // When scrolling down to the bottom of the container
    const handleHitBottom = () => {
        if (!isFetchingNextPage) {
            search(gifSearchValue, true);
        }
    };

    useEffect(() => {
        const slideJustBecameActive = !isPreviouslyActive && isActive;
        const noPreviousResults = !gifs.length && !gifSearchValue;

        if (slideJustBecameActive && noPreviousResults) {
            search();
        }
    }, [isActive, gifs]);

    useEffect(() => {
        // If search input prop changed, debounce a search.
        if (previousGifSearchValue !== gifSearchValue) {
            searchDebounced(gifSearchValue);
        }

        return () => searchDebounced.cancel();
    }, [gifSearchValue]);

    return (
        <Slide className={BASE_CLASS_NAME} isActive={isActive}>
            <SuiContainer variation="flex-vertical full-height">
                {isActive && (
                    <SearchBar
                        placeholder={SEARCH_PLACEHOLDER}
                        setSearchValue={setGifSearchValue}
                        searchValue={gifSearchValue}
                        qaId={BASE_CLASS_NAME}
                    />
                )}

                <SuiContainer variation="flex flex-vertical full-height">
                    {gifSearchError && !isSearching && (
                        <SuiContainer variation="flex-group">
                            <p>{gifSearchError}</p>
                        </SuiContainer>
                    )}

                    {!isSearching && !gifSearchError && !gifs.length && <NoResultsFound qaId={BASE_CLASS_NAME} />}

                    {!gifSearchError && gifs.length && (
                        <InfiniteScrollContainer
                            onHitBottom={handleHitBottom}
                            className={`${BASE_CLASS_NAME}__scroll-container ${BASE_CLASS_NAME}__scroll-container_gif-list`}
                            ref={gifListRef}
                            variation="flex-group flex-vertical"
                            grow={1}
                        >
                            {gifs.map((gif) => (
                                <Gif
                                    key={gif.id}
                                    alt={gif.title}
                                    className={`${BASE_CLASS_NAME}__scroll-container_gif`}
                                    src={gif.url}
                                    onClick={(event) => onGifSelected(gif)}
                                />
                            ))}
                        </InfiniteScrollContainer>
                    )}

                    {isSearching && isFetchingNextPage && (
                        <SuiContainer variation="flex-group flex-horizontal-centered padded">
                            <CubeLoader className={`${BASE_CLASS_NAME}__spinner`} size={INLINE_SIZE} />
                        </SuiContainer>
                    )}
                </SuiContainer>

                {isSearching && !isFetchingNextPage && (
                    <CubeLoaderOverlay loading size={MODAL_SIZE} backgroundColor={colorAppShadedBackground} />
                )}
            </SuiContainer>
        </Slide>
    );
}

GifSlide.propTypes = {
    isActive: PropTypes.bool,
    gifSearchValue: PropTypes.string.isRequired,
    gifs: PropTypes.array.isRequired,
    gifSearchError: PropTypes.string.isRequired,
    setGifSearchValue: PropTypes.func.isRequired,
    setGifs: PropTypes.func.isRequired,
    setGifSearchError: PropTypes.func.isRequired,
    onImageSelected: PropTypes.func.isRequired,
};

GifSlide.defaultProps = {
    isActive: true,
};
